import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as couponsApi from './discount-coupons.api';

import type {
  CreateDiscountCouponRequest,
  DiscountCouponListQuery,
  UpdateDiscountCouponRequest,
} from '@utazzo/shared';

const QK = {
  list: (query: DiscountCouponListQuery) => ['discount-coupons', 'list', query] as const,
  detail: (id: string) => ['discount-coupons', 'detail', id] as const,
  currencyOptions: () => ['currencies', 'options'] as const,
};

export const useDiscountCouponsList = (query: DiscountCouponListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => couponsApi.listDiscountCoupons(query),
    staleTime: 30_000,
  });

export const useCreateDiscountCoupon = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateDiscountCouponRequest) => couponsApi.createDiscountCoupon(payload),
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: ['discount-coupons', 'list'] });
      toast.success(`Added ${c.name}`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to add coupon'),
  });
};

export const useUpdateDiscountCoupon = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateDiscountCouponRequest }) =>
      couponsApi.updateDiscountCoupon(id, payload),
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: ['discount-coupons', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(c.id) });
      toast.success('Coupon updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update coupon'),
  });
};

export const useDeleteDiscountCoupon = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => couponsApi.deleteDiscountCoupon(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['discount-coupons', 'list'] });
      toast.success('Coupon deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete coupon'),
  });
};

export const useBulkDeleteDiscountCoupons = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => couponsApi.bulkDeleteDiscountCoupons(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['discount-coupons', 'list'] }),
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete coupons'),
  });
};

export const useBulkSetDiscountCouponsStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, isActive }: { ids: string[]; isActive: boolean }) =>
      couponsApi.bulkSetDiscountCouponsStatus(ids, isActive),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['discount-coupons', 'list'] }),
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update coupon status'),
  });
};

export const useCurrencyOptions = () =>
  useQuery({
    queryKey: QK.currencyOptions(),
    queryFn: couponsApi.listCurrencyOptions,
    // Currency list changes rarely; a long staleTime avoids redundant fetches
    // when the form is opened multiple times in a session.
    staleTime: 5 * 60_000,
  });

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};
